import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2,
  Plus,
  Trash2,
  Edit3,
  Save,
  X,
  ListChecks,
  Users,
  Truck,
  Route,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const TYPES = [
  { key: "consultant", label: "Consultores UniGR", icon: Users },
  { key: "fleet_type", label: "Tipos de frota", icon: Truck },
  { key: "operation_type", label: "Tipos de operação", icon: Route },
];

const AdminOptions = () => {
  const [active, setActive] = useState("consultant");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ value: "", order: 0 });

  const load = () => {
    setLoading(true);
    api
      .get(`/options?type=${active}`)
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [active]);

  const openNew = () => {
    setEditing(null);
    setForm({ value: "", order: items.length });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ value: it.value, order: it.order });
    setOpen(true);
  };

  const save = async () => {
    if (!form.value.trim()) {
      toast.error("Informe o valor.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type: active,
        value: form.value.trim(),
        order: Number(form.order) || 0,
      };
      if (editing) {
        await api.put(`/options/${editing.id}`, payload);
        toast.success("Opção atualizada.");
      } else {
        await api.post("/options", payload);
        toast.success("Opção criada.");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Excluir "${it.value}"?`)) return;
    try {
      await api.delete(`/options/${it.id}`);
      toast.success("Excluída.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const activeMeta = TYPES.find((t) => t.key === active);

  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.32em] text-[#6B8DB3]">
        Configuração
      </div>
      <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
        <ListChecks className="text-[#B9262B]" /> Listas suspensas
      </h1>
      <p className="text-[#9aa3ad] mt-1">
        Gerencie os valores das listas usadas no formulário público.
      </p>

      <div className="mt-6 flex flex-wrap gap-2">
        {TYPES.map((t) => (
          <button
            key={t.key}
            onClick={() => setActive(t.key)}
            data-testid={`option-tab-${t.key}`}
            className={[
              "px-4 py-2 rounded-lg text-sm transition-all border inline-flex items-center gap-2",
              active === t.key
                ? "bg-[#1C1F26] text-white border-[#B9262B]"
                : "bg-[#0e1015] text-[#9aa3ad] border-[#3A3F47] hover:border-[#6B8DB3]/60",
            ].join(" ")}
          >
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 flex justify-between items-center">
        <div className="text-sm text-[#6B8DB3]">
          {items.length} {items.length === 1 ? "item" : "itens"} em{" "}
          <span className="text-white">{activeMeta?.label}</span>
        </div>
        <Button
          data-testid="new-option-btn"
          onClick={openNew}
          className="bg-[#B9262B] hover:bg-[#9a1e22] text-white"
        >
          <Plus size={16} className="mr-2" /> Adicionar
        </Button>
      </div>

      <div className="mt-4 border border-[#3A3F47] rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#6B8DB3]">
            <Loader2 className="animate-spin inline mr-2" /> Carregando...
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-[#6B8DB3]">
            Nenhum item cadastrado nessa lista.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#0e1015] text-[#6B8DB3] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-4 w-16">Ord.</th>
                <th className="text-left p-4">Valor</th>
                <th className="text-right p-4 w-44"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr
                  key={it.id}
                  className="border-t border-[#1C1F26] hover:bg-[#1C1F26]/40"
                  data-testid={`option-row-${it.id}`}
                >
                  <td className="p-4 font-mono text-[#6B8DB3]">{it.order}</td>
                  <td className="p-4 text-white">{it.value}</td>
                  <td className="p-4 text-right">
                    <div className="inline-flex gap-2">
                      <button
                        onClick={() => openEdit(it)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs bg-[#1C1F26] hover:bg-[#252932] border border-[#3A3F47]"
                      >
                        <Edit3 size={14} /> Editar
                      </button>
                      <button
                        onClick={() => remove(it)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs bg-[#B9262B]/15 hover:bg-[#B9262B]/30 text-[#B9262B] border border-[#B9262B]/40"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#1C1F26] border-[#3A3F47] text-white max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Editar item" : `Novo item · ${activeMeta?.label}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider">
                Valor
              </Label>
              <Input
                data-testid="option-value-input"
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                className="bg-[#0e1015] border-[#3A3F47] text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider">
                Ordem
              </Label>
              <Input
                type="number"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: e.target.value })}
                className="bg-[#0e1015] border-[#3A3F47] text-white mt-1"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-[#3A3F47] text-white hover:bg-[#0e1015]"
            >
              <X size={14} className="mr-2" /> Cancelar
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="save-option-btn"
              className="bg-[#B9262B] hover:bg-[#9a1e22] text-white"
            >
              {saving ? (
                <Loader2 className="animate-spin" />
              ) : (
                <>
                  <Save size={14} className="mr-2" /> Salvar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOptions;
